/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * An adaptor video player that help to work with multiple type of video players with a single interface.
 *
 * To execute un-abstracted methods, get and execute from origin player from inner `player` property.
 *
 * @typedef {import('@vimeo/player').Player} VimeoPlayer
 */
export class Player {
  /**
   * Whether the player is ready
   *
   * @type {boolean}
   */
  isReady = false;

  /**
   *
   * @param {VimeoPlayer | YT.Player} player
   */
  constructor(player, type, options) {
    this.player = player;
    this.type = type;
    this.options = options;
  }

  /**
   * Add an event listener for the specified event.
   *
   * @param {string}   event    The event name
   * @param {function} listener The event listener
   *
   * @returns {void}
   */
  on(event, listener) {
    switch (this.type) {
      case 'vimeo': return this.player.on(event, listener);
      case 'youtube': return this.player.addEventListener(event, listener);
      case 'html': return this.player.addEventListener(event, listener);
    }
  }

  /**
   * Remove an event listener for the specified event.
   * Will remove all listeners for that event if a `listener` isn't passed
   *
   * @param {string}    event    The event name
   * @param {function=} listener The event listener
   *
   * @returns {void}
   */
  off(event, listener) {
    switch (this.type) {
      case 'vimeo': return this.player.off(event, listener);
      case 'youtube': return this.player.removeEventListener(event, listener);
      case 'html': return this.player.removeEventListener(event, listener);
    }
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id The video ID to load
   *
   * @returns {Promise}
   */
  async loadVideo(id) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      // case 'vimeo': return this.player.unload().then(() => this.player.loadVideo(id));
      case 'vimeo':
        return this.isMuted()
          .then(isMuted => {
            return this.player.unload()
              .then(() => this.player.loadVideo(id))
              .then(() => !isMuted && this.unMute())
            ;
          })
        ;

      case 'youtube':
        return this.stop()
          .then(() => this.player.loadPlaylist(id))
          .then(() => this.setLoop(!!this.options.loop))
        ;

      case 'html':
        return this.stop()
          .then(() => this.player.loadPlaylist(id))
          .then(() => this.setLoop(!!this.options.loop))
        ;
    }
  }

  /**
   * Gets the playing state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is playing.
   */
  async isPlaying() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return Promise
        .all([this.player.getPaused(), this.player.getEnded()])
        .then(([isPaused, isEnded]) => !isPaused && !isEnded)
      ;
      case 'youtube': return Promise.resolve(this.player.getPlayerState() === window.YT.PlayerState.PLAYING);
      case 'html': return Promise
        .all([this.player.getPaused(), this.player.getEnded()])
        .then(([isPaused, isEnded]) => !isPaused && !isEnded)
      ;
    }
  }

  /**
   * Gets the paused state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is paused.
   */
  async isPaused() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.getPaused();
      case 'youtube': return Promise.resolve(this.player.getPlayerState() === window.YT.PlayerState.PAUSED);
      case 'html': return this.player.getPaused();
    }
  }

  /**
   * Gets the ended state of the current player
   *
   * @returns {Promise<boolean>} `true` if playback is ended.
   */
  async isEnded() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.getEnded();
      case 'youtube': return Promise.resolve(this.player.getPlayerState() === window.YT.PlayerState.ENDED);
      case 'html': return this.player.getEnded();
    }
  }

  /**
   * Enable or disable the `autopause` behavior of this player.
   *
   * @param {boolean} autopause
   *
   * @returns {Promise}
   */
  async setAutopause(autopause) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.setAutopause(autopause);
      case 'youtube': return Promise.resolve(null);
      case 'html': return Promise.resolve(null);
    }
  }

  /**
   * Set the color of this player to a hex or rgb string.
   * Setting the color may fail if the owner of the video has set their embed preferences to force a specific color.
   *
   * @param {string} color
   *
   * @returns {Promise}
   */
  async setColor(color) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.setColor(color);
      case 'youtube': return Promise.resolve(null);
      case 'html': return Promise.resolve(null);
    }
  }

  /**
   * Set the loop state of the player.
   * When set to `true`, the player will start over immediately once playback ends.
   *
   * **Note:** when `loop` is turned on, the `ended` event will not fire for Vimeo player.
   *
   * @param {boolean} loop
   *
   * @returns {Promise}
   */
  async setLoop(loop) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.setLoop(loop);
      case 'youtube': return Promise.resolve(this.player.setLoop(loop));
      case 'html': return loop ? this.player.setAttribute('loop', '') : this.player.removeAttribute('loop');
    }
  }

  /**
   * Gets the muted status of the current player
   *
   * @returns {Promise<boolean>}
   */
  async isMuted() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.getMuted();
      case 'youtube': return Promise.resolve(this.player.isMuted());
      case 'html': return this.player.getMuted();
    }
  }

  /**
   * Set the muted state of the player.
   * When set to `true`, the player volume will be muted.
   *
   * @param {boolean} value
   *
   * @returns {Promise}
   */
  async setMuted(value) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    return value ? this.mute() : this.unMute();
  }

  /**
   * Set the playback rate of the player
   *
   * @param {number} value
   *
   * @returns {Promise}
   */
  async setPlaybackRate(value) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      // scale from 0.5 to 2
      case 'vimeo': return this.player.setPlaybackRate(value > 10 ? value / 100 : value);

      // 0.25, 0.5, 1, 1.5, and 2.
      case 'youtube': return Promise.resolve(this.player.setPlaybackRate(value > 10 ? value / 100 : value));

      // -1, -0.5, 0.5, 1, and 2.
      case 'html': return Promise.resolve(this.player.setPlaybackRate(value > 10 ? value / 100 : value));
    }
  }

  /**
   * Set the volume of the player
   *
   * @param {number} level
   *
   * @returns {Promise}
   */
  async setVolume(level) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      // scale from 0 to 1
      case 'vimeo': return this.player.setVolume(level > 1 ? level / 100 : level);

      // Accepts an integer between 0 and 100.
      case 'youtube': return Promise.resolve(this.player.setVolume(level > 1 ? level : level * 100));

      // scale from 0 to 1
      case 'html': return this.player.setAttribute('volumn', level > 1 ? level / 100 : level);
    }
  }

  /**
   * Unload the internal player (iframe) to its initial state.
   *
   * @returns {Promise}
   */
  async unload() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.unload();
      case 'youtube': return Promise.resolve(null);
      case 'html': return Promise.resolve(null);
    }
  }

  /**
   * Cleanup the player and remove it from the DOM.
   *
   * @returns {Promise}
   */
  async destroy() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.destroy();
      case 'youtube': return Promise.resolve(this.player.destroy());
      case 'html': return Promise.resolve(this.player.remove());
    }
  }

  /**
   * Mute the video
   *
   * @returns {Promise}
   */
  async mute() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.setMuted(true);
      case 'youtube': return Promise.resolve(this.player.mute());
      case 'html': return Promise.resolve(this.player.setAttribute('muted', ''));
    }
  }

  /**
   * Unmute the video
   *
   * @returns {Promise}
   */
  async unMute() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.setMuted(false);
      case 'youtube': return Promise.resolve(this.player.unMute());
      case 'html': return Promise.resolve(this.player.removeAttribute('muted'));
    }
  }

  /**
   * Plays the currently loaded video.
   *
   * @returns {Promise}
   */
  async play() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.play();
      case 'youtube': return Promise.resolve(this.player.playVideo());
      case 'html': return Promise.resolve(this.player.play());
    }
  }

  /**
   * Pause the video if it's playing.
   *
   * @returns {Promise}
   */
  async pause() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.pause();
      case 'youtube': return Promise.resolve(this.player.pauseVideo());
      case 'html': return Promise.resolve(this.player.pause());
    }
  }

  /**
   * Toggle the video playback.
   *
   * @returns {Promise}
   */
  async toggle() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    return this.isPlaying().then(isPlaying => isPlaying ? this.pause() : this.play());
  }

  /**
   * Stops and cancels loading of the current video.
   *
   * Vimeo player don't have `stop` function, it will behave using `pause` function.
   *
   * @returns {Promise}
   */
  async stop() {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    switch (this.type) {
      case 'vimeo': return this.player.pause();
      case 'youtube': return Promise.resolve(this.player.stopVideo());
      case 'html': return this.pause();
    }
  }

  /**
   * Call a method using directly inner player.
   * You should check the player type yourself before execute.
   *
   * @param {string}  method The method name
   * @param  {...any} args   List of arguments
   *
   * @returns {Promise}
   */
  async call(method, ...args) {
    if (!this.isReady) {
      await this.waitUntilReady();
    }

    return new Promise((resolve, reject) => {
      try {
        const result = this.player[method](...args);

        if (result instanceof Promise) {
          return result.then(resolve).catch(reject);
        }

        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Wait until the player is ready
   *
   * @private
   *
   * @returns {Promise}
   */
  waitUntilReady() {
    return new Promise((resolve) => {
      const check = () => this.isReady ? resolve() : requestAnimationFrame(check);

      check();
    });
  }
}
